import {
  Box,
  Flex,
  FormLabel,
  GridItem,
  HStack,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { FormContainer, FormControl } from '~/components/molecules';
import {
  Achievements,
  CertificationScoreForm,
  DesiredWorkForm,
  EducationHistoryForm,
  FocusForm,
  ProfileForm,
  SocialAccountForm,
} from '~/components/organisms';

import ChangeProfileImage from '~/components/molecules/ChangeProfileImage';
import { SocialAccount } from '~/generated';
import { mySocialAccounts } from '~/queries';
import FormTooltip from '../molecules/FormTooltip';

type HeaderContainerProps = {
  header: string;
  description?: string;
  tooltip?: ReactNode;
};

const HeaderContainer = ({
  header,
  description,
  tooltip,
}: HeaderContainerProps): JSX.Element => {
  const colorModeValue = useColorModeValue('gray.600', 'gray.400');

  return (
    <GridItem colSpan={{ md: 1 }}>
      <Box px={{ base: 0, md: 4 }}>
        <Flex sx={{ alignItems: 'center' }}>
          <Heading fontSize="lg" fontWeight="md" lineHeight="6" as="h2">
            {header}
          </Heading>
          {tooltip}
        </Flex>
        {description && (
          <Text
            color={colorModeValue}
            as="h3"
            sx={{
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              mt: 1,
              fontSize: 'sm',
            }}
          >
            {description}
          </Text>
        )}
      </Box>
    </GridItem>
  );
};

HeaderContainer.defaultProps = {
  description: undefined,
  tooltip: undefined,
};

const EditProfile: React.FC = () => {
  const { data, refetch } = useQuery<{
    jobSeekerMe: { socialAccounts: [SocialAccount] } | undefined;
  }>(mySocialAccounts);
  const modalBg = useColorModeValue('white', 'gray.700');

  return (
    <Box bg={useColorModeValue('gray.50', 'inherit')} p={{ base: 4, md: 10 }}>
      <Box>
        <SimpleGrid
          display={{ base: 'initial', md: 'grid' }}
          columns={{ md: 4 }}
          spacing={{ md: 6 }}
        >
          {/* <GridItem colSpan={{ md: 1 }}>
            <Box px={[4, 0]}>
              <Heading fontSize="lg" fontWeight="md" lineHeight="6">
                連絡先
              </Heading>
              <Text
                mt={1}
                fontSize="sm"
                color={useColorModeValue('gray.600', 'gray.400')}
              >
                ※非公開
              </Text>
            </Box>
          </GridItem>
          <GridItem mt={[5, null, 0]} colSpan={{ md: 3 }}>
            <Box
              shadow="base"
              rounded={[null, 'md']}
              overflow={{ sm: 'hidden' }}
            >
              <Stack
                px={4}
                py={5}
                bg={modalBg}
                spacing={6}
                p={{ sm: 8 }}
              >
                <ContactDetailsForm />
              </Stack>
            </Box>
          </GridItem> */}

          <HeaderContainer
            header="プロフィール"
            description="この情報は採用担当者に公開されます。"
          />
          <FormContainer>
            <Box
              shadow="base"
              rounded={[null, 'md']}
              overflow={{ sm: 'hidden' }}
            >
              <Stack px={4} py={5} bg={modalBg} spacing={6} p={{ sm: 8 }}>
                <FormControl>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="md"
                    color={useColorModeValue('gray.700', 'gray.50')}
                  >
                    画像
                  </FormLabel>
                  <Flex alignItems="center" mt={1}>
                    <HStack>
                      <ChangeProfileImage />
                      <FormTooltip>
                        <Text>
                          画像は自分自身が写り、ハッキリと分かる写真を選びましょう。
                        </Text>
                        <Text>
                          自分自身の個性が伝わる写真、スーツ着用の写真など自由に設定可能です。
                        </Text>
                        <Text>
                          ※
                          NG例・・・表情がわからない写真。複数名が写って誰が該当するのかわからない写真など。
                        </Text>
                      </FormTooltip>
                    </HStack>
                  </Flex>
                </FormControl>
              </Stack>
            </Box>
          </FormContainer>

          {data?.jobSeekerMe && (
            <>
              <HeaderContainer
                header="ソーシャルアカウント"
                description="※ Github、TwitterなどのSNSを企業人事に公開できます。"
              />
              <FormContainer>
                <Box
                  shadow="base"
                  rounded={[null, 'md']}
                  overflow={{ sm: 'hidden' }}
                >
                  <Stack px={4} py={5} bg={modalBg} spacing={6} p={{ sm: 8 }}>
                    <SocialAccountForm
                      socialAccounts={data.jobSeekerMe.socialAccounts}
                      refetch={refetch}
                    />
                  </Stack>
                </Box>
              </FormContainer>
            </>
          )}

          <HeaderContainer
            header="制作物"
            description="※ プレゼン資料、Webサイト、図面、動画などを公開できます。"
          />
          <FormContainer>
            <Box
              shadow="base"
              rounded={[null, 'md']}
              overflow={{ sm: 'hidden' }}
            >
              <Stack
                px={4}
                py={5}
                bg={modalBg}
                spacing={6}
                p={{ sm: 8 }}
                wrap="wrap"
              >
                <Achievements />
              </Stack>
            </Box>
          </FormContainer>

          <HeaderContainer header="基本情報" />
          <FormContainer>
            <Box
              shadow="base"
              rounded={[null, 'md']}
              overflow={{ sm: 'hidden' }}
            >
              <Stack px={4} py={5} bg={modalBg} spacing={6} p={{ sm: 8 }}>
                <ProfileForm />
              </Stack>
            </Box>
          </FormContainer>

          <HeaderContainer header="最終学歴" />
          <FormContainer>
            <EducationHistoryForm />
          </FormContainer>

          <HeaderContainer
            header="学生時代に力を入れたこと"
            tooltip={
              <FormTooltip>
                <Text>
                  アルバイト、部活・サークル、趣味、インターン、課外活動、もしくは学習内容や研究内容で記載しきれなかったことなど何でもOKです。
                </Text>
                <br />
                <Text>
                  「これだけはがんばった」と言える事を１つ記載しましょう（複数ある場合、書き方はおまかせします）
                </Text>
                <br />
                <Text>
                  記載例・・・概要、具体的な取り組み、直面した課題、得たこと、学んだこと、今後活かしたいことなど
                </Text>
              </FormTooltip>
            }
          />
          <FormContainer>
            <FocusForm />
          </FormContainer>

          <HeaderContainer header="保有資格" />
          <FormContainer>
            <Box
              shadow="base"
              rounded={[null, 'md']}
              overflow={{ sm: 'hidden' }}
              px={4}
              py={5}
              bg={modalBg}
              p={{ sm: 8 }}
            >
              <CertificationScoreForm />
            </Box>
          </FormContainer>

          <HeaderContainer
            header="働き方"
            tooltip={
              <FormTooltip display={{ base: 'block', md: 'none' }}>
                <Text>
                  希望職種、希望勤務地、希望企業規模をそれぞれ１つ～３つまで選択できます。希望に合ったスカウトを受信できる確率を高めるため、自分の中で決まっているものがあれば、選択することをお勧めします
                </Text>
                <br />
                <Text>
                  ※
                  一方で、まだ決まっておらず悩まれている場合、決まった後に選択しても大丈夫です。
                </Text>
              </FormTooltip>
            }
          />

          <FormContainer>
            <DesiredWorkForm />
          </FormContainer>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default EditProfile;
